import { useState } from 'react'
import { Crown, Flame, Medal, Sparkles, Target, Trophy } from 'lucide-react'

import { Button } from '@/components'
import { cn } from '@/utils/cn'

type LeaderboardTab = 'global' | 'children'

interface Leader {
  id: string
  rank: number
  name: string
  points: number
  classesCompleted: number
  lastActive: string
  avatarInitials: string
  highlight?: 'gold' | 'silver' | 'bronze'
  isChild?: boolean
  courseTitle?: string
  streakDays?: number
  badges?: number
  goalLabel?: string
}

const GLOBAL_LEADERS: Leader[] = [
  {
    id: '2',
    rank: 2,
    name: 'Alex Zhang',
    points: 5120,
    classesCompleted: 18,
    lastActive: 'Yesterday',
    avatarInitials: 'AZ',
    highlight: 'silver',
    courseTitle: 'Python for Beginners',
    streakDays: 32,
    badges: 18,
    goalLabel: 'Gold',
  },
  {
    id: '1',
    rank: 1,
    name: 'Sarah Johnson',
    points: 6850,
    classesCompleted: 22,
    lastActive: 'Today · 4:30 PM',
    avatarInitials: 'SJ',
    highlight: 'gold',
    isChild: true,
    courseTitle: 'Scratch Programming',
    streakDays: 45,
    badges: 28,
    goalLabel: 'Gold',
  },
  {
    id: '3',
    rank: 3,
    name: 'Sam Lee',
    points: 2450,
    classesCompleted: 10,
    lastActive: '2 days ago',
    avatarInitials: 'SL',
    highlight: 'bronze',
    courseTitle: 'Web Development',
    streakDays: 21,
    badges: 12,
    goalLabel: 'Silver',
  },
]

const CHILDREN_LEADERS: Leader[] = [
  {
    id: '1',
    rank: 1,
    name: 'Sarah Johnson',
    points: 6850,
    classesCompleted: 22,
    lastActive: 'Today · 4:30 PM',
    avatarInitials: 'SJ',
    highlight: 'gold',
    isChild: true,
    courseTitle: 'Scratch Programming',
    streakDays: 45,
    badges: 28,
    goalLabel: 'Gold',
  },
  {
    id: '2',
    rank: 2,
    name: 'Michael Johnson',
    points: 4280,
    classesCompleted: 16,
    lastActive: 'Today · 3:00 PM',
    avatarInitials: 'MJ',
    isChild: true,
    courseTitle: 'Python for Beginners',
    streakDays: 30,
    badges: 20,
    goalLabel: 'Gold',
  },
  {
    id: '3',
    rank: 3,
    name: 'Emily Johnson',
    points: 2680,
    classesCompleted: 11,
    lastActive: 'Yesterday',
    avatarInitials: 'EJ',
    isChild: true,
    courseTitle: 'Web Development',
    streakDays: 18,
    badges: 14,
    goalLabel: 'Silver',
  },
]

function rankHighlightClasses(rank: number, highlight?: Leader['highlight']) {
  if (highlight === 'gold' || rank === 1) {
    return 'border-amber-300 bg-amber-50'
  }
  if (highlight === 'silver' || rank === 2) {
    return 'border-gray-200 bg-gray-50'
  }
  if (highlight === 'bronze' || rank === 3) {
    return 'border-orange-100 bg-orange-50'
  }
  return 'border-gray-100 bg-white'
}

function avatarColor(rank: number) {
  if (rank === 1) return 'bg-amber-100 text-amber-800'
  if (rank === 2) return 'bg-slate-100 text-slate-700'
  if (rank === 3) return 'bg-orange-100 text-orange-700'
  return 'bg-gray-100 text-gray-700'
}

export const ParentLeaderboard: React.FC = () => {
  const [tab, setTab] = useState<LeaderboardTab>('global')

  const leaders = tab === 'global' ? GLOBAL_LEADERS : CHILDREN_LEADERS

  const myRank = CHILDREN_LEADERS[0]

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-gray-100 p-6 space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Leaderboard</h2>
            <p className="mt-1 text-sm text-gray-500">
              See how your children compare globally and within your family.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border-2 border-[#F7E0C9] bg-[#F9F7F5] p-6">
            <p className="text-base text-[#717392]">Highest rank</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">
              #{myRank.rank}{' '}
            </p>
            <span className="text-xs font-normal text-[#717392]">
              Among all students
            </span>
          </div>
          <div className="rounded-2xl border-2 border-[#D0EAD9] bg-[#F4F8F7] p-6">
            <p className="text-base text-gray-500">Total points</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">
              {myRank.points.toLocaleString()} pts
            </p>
            <span className="text-xs font-normal text-[#717392]">
              Combined family
            </span>
          </div>
          <div className="rounded-2xl border-2 border-[#C7D5E8] bg-[#F5F7F9] p-6">
            <p className="text-base text-[#717392]">Total badges</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">
              {myRank.classesCompleted}
            </p>
            <span className="text-xs font-normal text-[#717392]">
              Total badges
            </span>
          </div>
        </div>

        <div className="inline-flex rounded-2xl bg-gray-100 p-1 !mt-10">
          <button
            type="button"
            onClick={() => setTab('global')}
            className={cn(
              'flex items-center gap-2 rounded-lg px-4 py-1.5 text-xs font-medium transition-colors',
              tab === 'global'
                ? 'text-white bg-[#F28C28] shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            )}
          >
            <Trophy className="h-4 w-4" />
            Global leaderboard
          </button>
          <button
            type="button"
            onClick={() => setTab('children')}
            className={cn(
              'flex items-center gap-2 rounded-lg px-4 py-1.5 text-xs font-medium transition-colors',
              tab === 'children'
                ? 'bg-[#1D5AA6] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            )}
          >
            <Crown className="h-4 w-4" />
            My children only
          </button>
        </div>

        {/* Top 3 cards (global) + detailed rows (both tabs) */}
        {tab === 'global' && (
          <div className="grid gap-4 md:grid-cols-3">
            {GLOBAL_LEADERS.map((leader, index) => (
              <div
                key={leader.id}
                className={cn(
                  'flex flex-col items-center justify-center rounded-2xl px-4 py-5 text-center space-y-6',
                  rankHighlightClasses(leader.rank, leader.highlight),
                  index === 1
                    ? 'md:py-6 md:scale-[1.03] h-[400px] bg-gradient-to-b from-[#FAF1EB] to-[#FFFFFE] border-2 border-[#F28C28]'
                    : 'h-[300px] mt-10',
                  index === 0 ? 'bg-white border-2 border-[#E5E5E5]' : '',
                  index === 2
                    ? 'bg-gradient-to-b from-[#FFF8EE] to-[#FFFEFD] border-2 border-[#F28C28]'
                    : ''
                )}
              >
                <div
                  className={cn(
                    'mb-3 flex h-12 w-12 items-center justify-center rounded-full text-sm font-semibold shadow-sm',
                    avatarColor(leader.rank)
                  )}
                >
                  {leader.avatarInitials}
                </div>
                <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
                  {leader.rank === 1 && (
                    <Crown className="h-4 w-4 text-amber-500" />
                  )}
                  {leader.rank === 2 && (
                    <Medal className="h-4 w-4 text-slate-400" />
                  )}
                  {leader.rank === 3 && (
                    <Medal className="h-4 w-4 text-orange-400" />
                  )}
                  <span>Rank #{leader.rank}</span>
                </div>
                <p className="mt-1 text-sm font-semibold text-gray-900">
                  {leader.name}
                </p>
                {leader.isChild && (
                  <p className="mt-0.5 text-[11px] font-medium text-emerald-600">
                    My child
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  {leader.points.toLocaleString()} pts ·{' '}
                  {leader.classesCompleted} classes
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Bottom rows section */}
        <div className="mt-8 flex flex-col gap-3">
          {leaders.map(leader => (
            <div
              key={leader.id}
              className={cn(
                'flex flex-col gap-3 rounded-2xl border px-4 py-4 sm:px-5 sm:py-4 lg:flex-row lg:items-center shadow-sm bg-white',
                leader.isChild
                  ? 'border-[#F28C28] bg-[#FFF9F2]'
                  : 'border-gray-100'
              )}
            >
              {/* Left: icon + name + course */}
              <div className="flex flex-1 items-start gap-3">
                <div
                  className={cn(
                    'mt-0.5 flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold shadow-sm',
                    avatarColor(leader.rank)
                  )}
                >
                  {leader.rank}
                </div>
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    {leader.name}
                    {leader.isChild && (
                      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-medium text-emerald-800">
                        My child
                      </span>
                    )}
                  </p>
                  {leader.courseTitle && (
                    <p className="mt-0.5 text-[11px] text-gray-500">
                      {leader.courseTitle}
                    </p>
                  )}
                  <p className="mt-1 text-[11px] text-gray-500">
                    {leader.points.toLocaleString()} pts ·{' '}
                    {leader.classesCompleted} classes completed
                  </p>
                </div>
              </div>

              {/* Right: streak / badges / goal / points + CTA */}
              <div className="mt-2 flex items-center gap-4 lg:ml-auto lg:mt-0">
                {leader.streakDays != null && (
                  <div className="flex items-center gap-1 text-[11px] text-gray-600">
                    <Flame className="h-3.5 w-3.5 text-amber-500" />
                    <span className="font-semibold">{leader.streakDays}d</span>
                    <span>streak</span>
                  </div>
                )}
                {leader.badges != null && (
                  <div className="flex items-center gap-1 text-[11px] text-gray-600">
                    <Sparkles className="h-3.5 w-3.5 text-emerald-500" />
                    <span className="font-semibold">{leader.badges}</span>
                    <span>badges</span>
                  </div>
                )}
                {leader.goalLabel && (
                  <div className="flex items-center gap-1 text-[11px] text-gray-600">
                    <Target className="h-3.5 w-3.5 text-[#F28C28]" />
                    <span className="font-semibold">{leader.goalLabel}</span>
                    <span>goal</span>
                  </div>
                )}
                <div className="ml-2 text-right">
                  <p className="text-[11px] text-gray-500">Points</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {leader.points.toLocaleString()}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  className="border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  View progress
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
